import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { PROJECT_COLOUR_PALETTE } from '@/types/Project';

export type AddTaskDraft = {
  name: string;
};

export type AddTagDraft = {
  name: string;
};

export type ActiveSection = 'metadata' | 'tasks' | 'tags';

export type EditProjectDraft = {
  name: string;
  displayColour: string;
  defaultBillable: boolean;
};

export type RenameTagDraft = {
  name: string;
};

export type RenameTaskDraft = {
  name: string;
};

export type AdminProjectDetailPageState = {
  activeSection: ActiveSection;
  addTaskModalOpen: boolean;
  addTaskDraft: AddTaskDraft;
  addTagModalOpen: boolean;
  addTagDraft: AddTagDraft;
  editProjectModalOpen: boolean;
  editProjectDraft: EditProjectDraft;
  // UC-45 — archive confirmation dialog.
  pendingArchiveConfirm: boolean;
  // UC-46 — hard-delete confirmation.
  pendingDeleteConfirm: boolean;
  // UC-47 — tasks filter and sort (reload-volatile; not persisted).
  tasksFilters: { nameSearch: string; archiveStatus: 'all' | 'active' | 'archived' };
  tasksSort: { direction: 'asc' | 'desc' };
  // UC-49 — inline task rename state (single-instance scalar per brief §5.web-portal.7 #5).
  renamingTaskId: string | null;
  renameTaskDraft: RenameTaskDraft | null;
  // UC-50 — archive-task confirmation dialog.
  pendingTaskArchiveConfirm: { taskId: string; taskName: string } | null;
  // UC-51 — tags filter (reload-volatile; not persisted).
  tagsFilters: { nameSearch: string };
  // UC-53 — inline rename state.
  renamingTagId: string | null;
  renameTagDraft: RenameTagDraft | null;
  // UC-54 — delete tag confirmation dialog.
  pendingTagDeleteConfirm: { tagId: string; tagName: string } | null;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminProjectDetailPageState = {
  activeSection: 'metadata',
  addTaskModalOpen: false,
  addTaskDraft: { name: '' },
  addTagModalOpen: false,
  addTagDraft: { name: '' },
  editProjectModalOpen: false,
  editProjectDraft: {
    name: '',
    displayColour: PROJECT_COLOUR_PALETTE[0],
    defaultBillable: false,
  },
  pendingArchiveConfirm: false,
  pendingDeleteConfirm: false,
  tasksFilters: { nameSearch: '', archiveStatus: 'all' },
  tasksSort: { direction: 'asc' },
  renamingTaskId: null,
  renameTaskDraft: null,
  pendingTaskArchiveConfirm: null,
  tagsFilters: { nameSearch: '' },
  renamingTagId: null,
  renameTagDraft: null,
  pendingTagDeleteConfirm: null,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminProjectDetailPageSlice = createSlice({
  name: 'adminProjectDetailPage',
  initialState,
  reducers: {
    openAddTaskModal(state) {
      state.addTaskModalOpen = true;
      state.addTaskDraft = { name: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeAddTaskModal(state) {
      state.addTaskModalOpen = false;
      state.addTaskDraft = { name: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setAddTaskDraftName(state, action: PayloadAction<string>) {
      state.addTaskDraft.name = action.payload;
    },
    openAddTagModal(state) {
      state.addTagModalOpen = true;
      state.addTagDraft = { name: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeAddTagModal(state) {
      state.addTagModalOpen = false;
      state.addTagDraft = { name: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setAddTagDraftName(state, action: PayloadAction<string>) {
      state.addTagDraft.name = action.payload;
    },
    setTasksFilterNameSearch(state, action: PayloadAction<string>) {
      state.tasksFilters.nameSearch = action.payload;
    },
    setTasksFilterArchiveStatus(state, action: PayloadAction<'all' | 'active' | 'archived'>) {
      state.tasksFilters.archiveStatus = action.payload;
    },
    setTasksSortDirection(state, action: PayloadAction<'asc' | 'desc'>) {
      state.tasksSort.direction = action.payload;
    },
    setTagsFilterNameSearch(state, action: PayloadAction<string>) {
      state.tagsFilters.nameSearch = action.payload;
    },
    setActiveSection(state, action: PayloadAction<ActiveSection>) {
      state.activeSection = action.payload;
    },
    openEditProjectModal(state, action: PayloadAction<EditProjectDraft>) {
      state.editProjectModalOpen = true;
      state.editProjectDraft = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeEditProjectModal(state) {
      state.editProjectModalOpen = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setEditProjectName(state, action: PayloadAction<string>) {
      state.editProjectDraft.name = action.payload;
    },
    setEditProjectDisplayColour(state, action: PayloadAction<string>) {
      state.editProjectDraft.displayColour = action.payload;
    },
    setEditProjectDefaultBillable(state, action: PayloadAction<boolean>) {
      state.editProjectDraft.defaultBillable = action.payload;
    },
    // ── UC-45 archive confirm ─────────────────────────────────────────────
    openArchiveConfirm(state) {
      state.pendingArchiveConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeArchiveConfirm(state) {
      state.pendingArchiveConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-46 delete-project confirm ──────────────────────────────────────
    openDeleteProjectConfirm(state) {
      state.pendingDeleteConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDeleteProjectConfirm(state) {
      state.pendingDeleteConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-49 rename-task ─────────────────────────────────────────────────
    startRenameTask(state, action: PayloadAction<{ taskId: string; currentName: string }>) {
      state.renamingTaskId = action.payload.taskId;
      state.renameTaskDraft = { name: action.payload.currentName };
      state.lastErrorKey = null;
    },
    setRenameTaskDraftName(state, action: PayloadAction<string>) {
      if (state.renameTaskDraft) {
        state.renameTaskDraft.name = action.payload;
      }
    },
    cancelRenameTask(state) {
      state.renamingTaskId = null;
      state.renameTaskDraft = null;
      state.lastErrorKey = null;
    },
    finishRenameTask(state) {
      state.renamingTaskId = null;
      state.renameTaskDraft = null;
      state.lastErrorKey = null;
      state.submitInProgress = false;
    },
    // ── UC-50 archive-task confirm ────────────────────────────────────────
    openTaskArchiveConfirm(state, action: PayloadAction<{ taskId: string; taskName: string }>) {
      state.pendingTaskArchiveConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeTaskArchiveConfirm(state) {
      state.pendingTaskArchiveConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-54 delete-tag confirm ──────────────────────────────────────────
    openDeleteTagConfirm(state, action: PayloadAction<{ tagId: string; tagName: string }>) {
      state.pendingTagDeleteConfirm = action.payload;
      state.lastErrorKey = null;
    },
    closeDeleteTagConfirm(state) {
      state.pendingTagDeleteConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-53 rename-tag ──────────────────────────────────────────────────
    startRenameTag(state, action: PayloadAction<{ tagId: string; currentName: string }>) {
      state.renamingTagId = action.payload.tagId;
      state.renameTagDraft = { name: action.payload.currentName };
      state.lastErrorKey = null;
    },
    setRenameTagDraftName(state, action: PayloadAction<string>) {
      if (state.renameTagDraft) {
        state.renameTagDraft.name = action.payload;
      }
    },
    cancelRenameTag(state) {
      state.renamingTagId = null;
      state.renameTagDraft = null;
      state.lastErrorKey = null;
    },
    finishRenameTag(state) {
      state.renamingTagId = null;
      state.renameTagDraft = null;
      state.lastErrorKey = null;
      state.submitInProgress = false;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  openAddTaskModal,
  closeAddTaskModal,
  setAddTaskDraftName,
  openAddTagModal,
  closeAddTagModal,
  setAddTagDraftName,
  setTasksFilterNameSearch,
  setTasksFilterArchiveStatus,
  setTasksSortDirection,
  setTagsFilterNameSearch,
  setActiveSection,
  openEditProjectModal,
  closeEditProjectModal,
  setEditProjectName,
  setEditProjectDisplayColour,
  setEditProjectDefaultBillable,
  openArchiveConfirm,
  closeArchiveConfirm,
  openDeleteProjectConfirm,
  closeDeleteProjectConfirm,
  startRenameTask,
  setRenameTaskDraftName,
  cancelRenameTask,
  finishRenameTask,
  openTaskArchiveConfirm,
  closeTaskArchiveConfirm,
  openDeleteTagConfirm,
  closeDeleteTagConfirm,
  startRenameTag,
  setRenameTagDraftName,
  cancelRenameTag,
  finishRenameTag,
  setSubmitInProgress,
  setLastErrorKey,
  resetPage,
} = adminProjectDetailPageSlice.actions;

type AppRootState = {
  adminProjectDetailPage: AdminProjectDetailPageState;
};

export const selectAdminProjectDetailPage = (state: AppRootState) =>
  state.adminProjectDetailPage;
